import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Star, Store, DollarSign, User, Trash2, ShoppingCart, Image } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddFavoriteDialog from './AddFavoriteDialog';
import AddToListDialog from './AddToListDialog';

// Green theme tokens
const THEME = {
  primary: '#86EFAC',
  light: '#DCFCE7',
  lighter: '#F0FDF4',
  dark: '#166534',
  icon: '#22C55E',
  border: '#D1FAE5',
  surface: '#FAFDF7',
  star: '#F59E0B',
  starLight: '#FEF3C7',
};

const FavoritesTab = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [selectedFavorite, setSelectedFavorite] = useState(null);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await API.get('/shopping/favorites');
      setFavorites(response.data);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this favorite?')) return;
    try {
      await API.delete(`/shopping/favorites/${id}`);
      toast.success('Favorite deleted');
      fetchFavorites();
    } catch (error) {
      toast.error('Failed to delete favorite');
    }
  };

  const handleAddToList = (favorite) => {
    setSelectedFavorite(favorite);
    setAddToListOpen(true);
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: 'Active', bg: THEME.light, text: THEME.dark },
      discontinued: { label: 'Discontinued', bg: '#F3F4F6', text: '#6B7280' },
      cant_find: { label: "Can't Find", bg: '#FEF3C7', text: '#92400E' }
    };
    const cfg = config[status] || config.active;
    return (
      <Badge style={{ backgroundColor: cfg.bg, color: cfg.text }}>
        {cfg.label}
      </Badge>
    );
  };

  const getCategoryColor = (category) => {
    const colors = {
      food: { bg: '#DCFCE7', text: '#166534' },
      household: { bg: '#DBEAFE', text: '#1E40AF' },
      personal_care: { bg: '#EDE9FE', text: '#5B21B6' },
      pet: { bg: '#FEF3C7', text: '#92400E' },
      other: { bg: '#F3F4F6', text: '#6B7280' }
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <h3 
            className="text-xl font-semibold"
            style={{ color: THEME.dark }}
          >
            Favorites
          </h3>
          <p className="text-sm text-gray-500 mt-1">Products you love and want to buy again</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow"
          style={{ 
            backgroundColor: THEME.starLight,
            color: '#92400E',
          }}
          data-testid="add-favorite-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Favorite
        </Button>
      </div>

      {favorites.length === 0 ? (
        <div 
          className="text-center py-16 rounded-2xl border-2 border-dashed"
          style={{ 
            backgroundColor: THEME.starLight,
            borderColor: '#FDE68A',
          }}
        >
          <div 
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: '#FEF9C3' }}
          >
            <Star className="h-8 w-8" style={{ color: THEME.star }} />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Save Your Favorites</h4>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            Save products you love so you can find them again
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="shadow-sm hover:shadow-md transition-shadow"
            style={{ 
              backgroundColor: THEME.starLight,
              color: '#92400E',
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Favorite
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {favorites.map((fav) => {
            const categoryColor = getCategoryColor(fav.category);
            return (
              <Card 
                key={fav.id} 
                className="overflow-hidden hover:shadow-lg transition-all duration-200"
                style={{ 
                  borderColor: '#FDE68A',
                  borderWidth: '1px',
                  borderLeftWidth: '4px',
                  borderLeftColor: THEME.star,
                }}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <h4 className="font-semibold text-gray-900">{fav.name}</h4>
                        {getStatusBadge(fav.status)}
                      </div>
                      {fav.brand && (
                        <p className="text-sm text-gray-500">{fav.brand}</p>
                      )}
                    </div>
                    {fav.photos && fav.photos.length > 0 && (
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: THEME.lighter }}
                      >
                        <Image className="h-5 w-5" style={{ color: THEME.dark }} />
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <Badge 
                      className="text-xs font-medium"
                      style={{ 
                        backgroundColor: categoryColor.bg, 
                        color: categoryColor.text 
                      }}
                    >
                      {fav.category.replace('_', ' ')}
                    </Badge>
                    
                    {fav.identification_notes && (
                      <p 
                        className="text-sm p-3 rounded-lg"
                        style={{ backgroundColor: THEME.lighter, color: '#4B5563' }}
                      >
                        {fav.identification_notes}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      {fav.store && (
                        <span className="flex items-center gap-1.5">
                          <Store className="h-3.5 w-3.5" /> {fav.store}
                        </span>
                      )}
                      {fav.typical_price && (
                        <span className="flex items-center gap-1.5">
                          <DollarSign className="h-3.5 w-3.5" /> ${fav.typical_price}
                        </span>
                      )}
                      {fav.who_likes && (
                        <span className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5" /> {fav.who_likes}
                        </span>
                      )}
                    </div>
                    
                    {fav.why_like && (
                      <p 
                        className="text-xs italic"
                        style={{ color: THEME.dark }}
                      >
                        {fav.why_like}
                      </p>
                    )}
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex gap-2 mt-5 pt-4 border-t border-gray-100">
                    <Button
                      onClick={() => handleAddToList(fav)}
                      className="flex-1 shadow-sm hover:shadow-md transition-shadow"
                      style={{ 
                        backgroundColor: THEME.primary,
                        color: THEME.dark,
                      }}
                      size="sm"
                      data-testid={`add-to-list-${fav.id}`}
                    >
                      <ShoppingCart className="h-4 w-4 mr-1.5" />
                      Add to List
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(fav.id)}
                      className="text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                      data-testid={`delete-favorite-${fav.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AddFavoriteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => {
          fetchFavorites();
          setDialogOpen(false);
        }}
      />
      
      <AddToListDialog
        open={addToListOpen}
        onOpenChange={setAddToListOpen}
        favorite={selectedFavorite}
        onSuccess={() => {
          setAddToListOpen(false);
          setSelectedFavorite(null);
        }}
      />
    </>
  );
};

export default FavoritesTab;
